/**
 * @fileoverview Hook de Data Layer para obtener y serializar la lista de conversaciones del inbox en Mercately CRM, soportando paginación incremental.
 * Utiliza Redux para acceder al estado y aplica serialización optimizada para la UI.
 * @module useInboxChatListData
 * @category Hook - Data Layer
 * @since 2.0.0
 * @version 3.2.0
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { serializeConversation } from '../../../utils/conversationUtils';

/**
 * Hook de Data Layer para obtener la lista de conversaciones del inbox, serializadas para la UI y soportando paginación incremental.
 *
 * @function useInboxChatListData
 * @param {number} [page=1] - Número de página a consultar (por defecto 1)
 * @returns {{ customerConversations: SerializedConversation[], pagination: Object }}
 *   Objeto con la lista de conversaciones serializadas y datos de paginación
 *
 * @see SerializedConversation
 * @see serializeConversation
 *
 * @example
 * // Obtener página 1
 * const { customerConversations } = useInboxChatListData();
 * // Obtener página 2
 * const { customerConversations } = useInboxChatListData(2);
 *
 * @since 2.0.0
 * @version 3.2.0
 */
export default function useInboxChatListData() {
  // Obtenemos los datos del estado que necesitamos
  const conversations = useSelector((state) => 
    state.conversationsReducer.data || []
  );
  const pagination = useSelector((state) => state.conversationsReducer.pagination || {});
  const currentRetailerUser = useSelector((state) => state.mainReducer.currentRetailerUser);
  const inboxAgentsReducer = useSelector((state) => state.inboxAgentsReducer);
  const aiCopilotsEnabled = useSelector(
    (state) => state.retailerUsersReducer.retailer_info?.ai_copilots_enabled
  );

  /**
   * Lista de conversaciones serializadas memoizada.
   * @type {SerializedConversation[]}
   */
  const customerConversations = useMemo(() => {
    if (!Array.isArray(conversations)) return [];
    const options = { aiCopilotsEnabled };
    return conversations.map(
      (conversation) => serializeConversation(conversation, currentRetailerUser, inboxAgentsReducer, options)
    );
  }, [conversations, currentRetailerUser, inboxAgentsReducer, aiCopilotsEnabled]);

  // Retorno con paginación
  return useMemo(() => ({ customerConversations, pagination }), [customerConversations, pagination]);
}
